import { useState, useEffect, useCallback, ReactNode } from 'react';
import { supabaseClient, shouldUseSupabase } from '@/lib/supabaseClient';
import {
  getOrCreateSessionId,
  getCurrentSessionId,
  fetchCartView,
  addItemToCart,
  updateItemQuantity,
  removeItemFromCart,
  clearCartItems,
  changeCartItemVariant,
  updateBundleQuantityRPC,
  removeBundleFromCart,
  CartViewRow,
} from '@/services/cartService';
import Toast from '@/components/base/Toast';
import { useToast } from '@/hooks/useToast';
import { useCartAbandon } from '@/hooks/useCartAbandon';
import { gaAddToCart, gaRemoveFromCart } from '@/utils/analytics';

/* ------------------------------------------------------------------ */
/*  Tipos públicos del contexto                                       */
/* ------------------------------------------------------------------ */

export interface CartItem {
  /** UUID del registro en cart_items o cart_bundles */
  id: string;
  /** 'product' | 'bundle' */
  itemType: 'product' | 'bundle';
  /** product_id numérico (solo para productos) */
  productId: number;
  /** variant_id UUID (solo para productos) */
  variantId: string;
  name: string;
  price: string;
  image: string;
  quantity: number;
  category?: string;
  variantLabel?: string;
  unitPrice: number;
  stock: number;
  /** Bundle-specific fields */
  bundleId?: number;
  bundleSlug?: string;
  originalPrice?: number | null;
  /** Snapshot price fields */
  originalUnitPrice?: number | null;
  priceLockedAt?: string | null;
  /** line_total calculado por la BD (unit_price * quantity) */
  lineTotal?: number | null;
}

export interface AppliedCoupon {
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  minPurchase: number;
  description?: string;
}

interface CouponResult {
  success: boolean;
  message: string;
}

export interface CartContextType {
  items: CartItem[];
  addToCart: (product: {
    id: number;
    name: string;
    price: string;
    image: string;
    category?: string;
    variantId?: string;
  }, quantity?: number) => Promise<void>;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  appliedCoupon: AppliedCoupon | null;
  discount: number;
  applyCoupon: (code: string) => Promise<CouponResult>;
  removeCoupon: () => void;
  couponLoading: boolean;
  sessionId: string | null;
  cartLoading: boolean;
  cartError: string | null;
  refreshCart: () => Promise<void>;
  changeVariant: (itemId: string, newVariantId: string, currentQuantity: number) => Promise<void>;
  isAdding: boolean;
  cartCount: number;
}

import { CartContext } from './CartContextValue';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function mapViewRowToCartItem(row: CartViewRow): CartItem {
  if (row.item_type === 'bundle') {
    // ✅ SIEMPRE usar unit_price guardado en cart_bundles (snapshot real de la BD)
    // Nunca usar bundle_price (precio en tiempo real que puede diferir)
    const snapshotPrice = row.unit_price != null ? Number(row.unit_price) : 0;
    // line_total viene calculado por la BD (unit_price * quantity); si es null, calcular localmente
    const lineTotal = row.line_total != null ? Number(row.line_total) : snapshotPrice * row.quantity;

    return {
      id: row.id,
      itemType: 'bundle',
      productId: 0,
      variantId: '',
      name: row.bundle_name || 'Pack',
      price: snapshotPrice.toFixed(2).replace('.', ','),
      image: row.bundle_image || '',
      quantity: row.quantity,
      category: 'pack',
      variantLabel: undefined,
      unitPrice: snapshotPrice,
      stock: 9999,
      originalPrice: null,
      bundleId: row.bundle_id ?? undefined,
      bundleSlug: row.bundle_slug ?? undefined,
      originalUnitPrice: row.original_unit_price != null ? Number(row.original_unit_price) : null,
      priceLockedAt: row.price_locked_at ?? null,
      lineTotal,
    };
  }

  // Para productos, usar unit_price (snapshot) en lugar de variant_price
  const snapshotPrice = row.unit_price != null ? Number(row.unit_price) : (row.variant_price != null ? Number(row.variant_price) : 0);
  const originalPrice = row.original_unit_price != null ? Number(row.original_unit_price) : (row.product_original_price != null ? Number(row.product_original_price) : null);
  const lineTotal = row.line_total != null ? Number(row.line_total) : snapshotPrice * row.quantity;

  return {
    id: row.id,
    itemType: 'product',
    productId: row.product_id ?? 0,
    variantId: row.variant_id ?? '',
    name: row.product_name ?? '',
    price: snapshotPrice.toFixed(2).replace('.', ','),
    image: row.product_image ?? '',
    quantity: row.quantity,
    category: row.product_category ?? '',
    variantLabel: row.variant_label ?? undefined,
    unitPrice: snapshotPrice,
    stock: row.variant_stock ?? 0,
    originalPrice,
    originalUnitPrice: originalPrice,
    priceLockedAt: row.price_locked_at ?? null,
    lineTotal,
  };
}

/* ------------------------------------------------------------------ */
/*  Provider                                                          */
/* ------------------------------------------------------------------ */

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(getCurrentSessionId());
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartLoading, setCartLoading] = useState(true);
  const [cartError, setCartError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const { toasts, showToast, removeToast } = useToast();
  
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const saved = localStorage.getItem('appliedCoupon');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [couponLoading, setCouponLoading] = useState(false);

  // Persist applied coupon
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (appliedCoupon) {
      try {
        localStorage.setItem('appliedCoupon', JSON.stringify(appliedCoupon));
      } catch {
        // ignore storage errors
      }
    } else {
      localStorage.removeItem('appliedCoupon');
    }
  }, [appliedCoupon]);

  /* ---- Cargar carrito desde cart_view ---- */
  useEffect(() => {
    const notifyRenewal = () => {
      showToast({ type: 'info', message: 'Hemos renovado tu carrito. Si tenías productos como invitado, vuelve a añadirlos.', duration: 8000 });
    };
    window.addEventListener('cart-session-renewed', notifyRenewal);
    return () => window.removeEventListener('cart-session-renewed', notifyRenewal);
  }, [showToast]);

  const refreshCart = useCallback(async () => {
    setCartLoading(true);
    setCartError(null);
    try {
      const sid = getCurrentSessionId() ? await getOrCreateSessionId() : null;
      setSessionId(sid);
      if (!sid || !shouldUseSupabase()) {
        setItems([]);
        return;
      }
      const rows = await fetchCartView(sid);
      setItems(rows.map(mapViewRowToCartItem));
    } catch {
      setCartError('No se pudo recuperar el carrito. Reintenta antes de continuar con la compra.');
    } finally {
      setCartLoading(false);
    }
  }, []);

  // Cargar al montar
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  // Sincronizar sessionId cuando la ventana recupera el foco
  // (cubre el caso de que una pestaña diferente haya actualizado localStorage)
  useEffect(() => {
    const syncSession = () => {
      const sid = getCurrentSessionId();
      setSessionId(sid);
    };
    window.addEventListener('focus', syncSession);
    return () => window.removeEventListener('focus', syncSession);
  }, []);

  // Refrescar carrito tras login
  useEffect(() => {
    if (!supabaseClient) return;
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        setTimeout(() => {
          refreshCart();
        }, 500);
      }
    });
    return () => subscription.unsubscribe();
  }, [refreshCart]);

  /* ---- addToCart (solo productos) ---- */
  const addToCart = useCallback(
    async (product: {
      id: number;
      name: string;
      price: string;
      image: string;
      category?: string;
      variantId?: string;
    }, quantity: number = 1) => {
      if (isAdding) return;

      // Validar que viene variantId — nunca añadir por product_id
      if (!product.variantId) {
        showToast({ type: 'error', message: 'Selecciona un formato antes de añadir al carrito', duration: 3000 });
        return;
      }

      setIsAdding(true);

      try {
        const sid = await getOrCreateSessionId();
        setSessionId(sid);

        let userId: string | null = null;
        if (supabaseClient) {
          const { data: { user } = {} } = await supabaseClient.auth.getUser();
          userId = user?.id ?? null;
        }

        await addItemToCart(sid, product.variantId, quantity, userId);
        await refreshCart();

        // Tracking GA4 — falla silenciosamente si hay error de red
        const priceNum = parseFloat(product.price.replace(',', '.')) || 0;
        gaAddToCart({ item_id: product.id, item_name: product.name, value: priceNum * quantity });

        showToast({ type: 'success', message: '¡Añadido al carrito!', duration: 2000 });
        setIsCartOpen(true);
      } catch {
        showToast({ type: 'error', message: 'No se pudo añadir al carrito', duration: 3000 });
      } finally {
        setIsAdding(false);
      }
    },
    [isAdding, refreshCart, showToast],
  );

  /* ---- removeFromCart (productos y bundles) ---- */
  const removeFromCart = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);

      // Tracking remove_from_cart — falla silenciosamente
      if (item && item.itemType === 'product') {
        try {
          gaRemoveFromCart({
            item_id: item.productId,
            item_name: item.name,
            value: item.unitPrice * item.quantity,
            item_category: item.category,
            quantity: item.quantity,
          });
        } catch {
          // silencioso
        }
      }

      try {
        // Optimistic update
        setItems((prev) => prev.filter((i) => i.id !== id));

        if (item?.itemType === 'bundle') {
          await removeBundleFromCart(id);
        } else {
          await removeItemFromCart(id);
        }
      } catch {
        await refreshCart();
      }
    },
    [items, refreshCart],
  );

  /* ---- updateQuantity (productos y bundles) ---- */
  const updateQuantity = useCallback(
    async (id: string, quantity: number) => {
      if (quantity < 1) {
        await removeFromCart(id);
        return;
      }

      const item = items.find((i) => i.id === id);
      if (!item) return;

      // Optimistic update: recalcular lineTotal con unit_price * nueva cantidad
      // unit_price viene de la BD (snapshot real), nunca de bundle_price
      const optimisticLineTotal = item.unitPrice * quantity;

      setItems((prev) =>
        prev.map((i) =>
          i.id === id
            ? { ...i, quantity, lineTotal: optimisticLineTotal }
            : i,
        ),
      );

      try {
        if (item.itemType === 'bundle' && item.bundleSlug) {
          const sid = getCurrentSessionId();
          if (!sid) return;

          let userId: string | null = null;
          if (supabaseClient) {
            const { data: { user } = {} } = await supabaseClient.auth.getUser();
            userId = user?.id ?? null;
          }

          const delta = quantity - item.quantity;
          await updateBundleQuantityRPC(sid, item.bundleSlug, delta, userId);
        } else {
          await updateItemQuantity(id, quantity);
        }

        // Refrescar desde BD para sincronizar line_total real devuelto por cart_view
        await refreshCart();
      } catch {
        // Revertir optimistic update si falla
        await refreshCart();
      }
    },
    [items, removeFromCart, refreshCart],
  );

  /* ---- clearCart ---- */
  const clearCart = useCallback(async () => {
    const sid = getCurrentSessionId();
    setItems([]);
    setAppliedCoupon(null);
    if (sid) {
      await clearCartItems(sid);
    }
    setSessionId(null);
  }, []);

  /* ---- changeVariant (solo productos) ---- */
  const changeVariant = useCallback(
    async (itemId: string, newVariantId: string, currentQuantity: number) => {
      const sid = getCurrentSessionId();
      if (!sid) return;
      try {
        await changeCartItemVariant(itemId, sid, newVariantId, currentQuantity);
        await refreshCart();
      } catch {
        await refreshCart();
      }
    },
    [refreshCart],
  );

  /* ---- Totales ---- */
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  // Calcular subtotal usando line_total de BD cuando esté disponible
  // Fallback: unit_price * quantity (para el optimistic update)
  const totalPrice = items.reduce((sum, item) => {
    const lt = item.lineTotal != null ? item.lineTotal : item.unitPrice * item.quantity;
    return sum + lt;
  }, 0);

  const cartCount = items.reduce((sum, item) => sum + (item.quantity ?? 1), 0);

  // Descuento de cupón (no recalcular descuentos de productos - ya están en unit_price)
  const discount = (() => {
    if (!appliedCoupon) return 0;
    if (totalPrice < appliedCoupon.minPurchase) return 0;
    if (appliedCoupon.type === 'percentage') {
      return Math.round(totalPrice * (appliedCoupon.value / 100) * 100) / 100;
    }
    return Math.min(appliedCoupon.value, totalPrice);
  })();

  // Auto-remove coupon if cart no longer meets the minimum purchase requirement
  useEffect(() => {
    if (appliedCoupon && totalPrice < appliedCoupon.minPurchase && totalPrice > 0) {
      setAppliedCoupon(null);
    }
  }, [totalPrice, appliedCoupon]);

  /* ---- Cupones ---- */
  const applyCoupon = async (code: string): Promise<CouponResult> => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return { success: false, message: 'Introduce un código de cupón' };
    if (appliedCoupon) return { success: false, message: 'Ya tienes un cupón aplicado. Elimínalo primero.' };
    if (!supabaseClient) return { success: false, message: 'Error de configuración. Inténtalo más tarde.' };

    setCouponLoading(true);
    try {
      const { data: coupon, error } = await supabaseClient
        .from('coupons')
        .select('*')
        .eq('code', trimmed)
        .eq('active', true)
        .maybeSingle();

      if (error || !coupon) return { success: false, message: 'El código de cupón no es válido' };
      if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) return { success: false, message: 'Este cupón ha expirado' };
      if (coupon.max_uses > 0 && coupon.used_count >= coupon.max_uses) return { success: false, message: 'Este cupón ya no está disponible' };

      const minPurchase = Number(coupon.min_purchase) || 0;
      if (totalPrice < minPurchase) {
        return {
          success: false,
          message: `Compra mínima de ${minPurchase.toFixed(2).replace('.', ',')} € para usar este cupón`,
        };
      }

      setAppliedCoupon({
        code: coupon.code,
        type: coupon.type as 'percentage' | 'fixed',
        value: Number(coupon.value),
        minPurchase,
        description: coupon.description || undefined,
      });

      return { success: true, message: '¡Cupón aplicado correctamente!' };
    } catch {
      return { success: false, message: 'Error al validar el cupón. Inténtalo de nuevo.' };
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => setAppliedCoupon(null);

  // Tracking de carrito abandonado
  useCartAbandon({ cartCount, subtotal: totalPrice - discount });

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
        isCartOpen,
        setIsCartOpen,
        appliedCoupon,
        discount,
        applyCoupon,
        removeCoupon,
        couponLoading,
        sessionId,
        cartLoading,
        cartError,
        refreshCart,
        changeVariant,
        isAdding,
        cartCount,
      }}
    >
      {children}
      
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          type={toast.type}
          message={toast.message}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </CartContext.Provider>
  );
};

